/**
 * The 🔤 tab: Letters and Words, side by side
 * ([Review](../../../docs/REVIEW.md#where-it-lives), [D-137](../../../docs/DECISIONS.md#d-137)).
 *
 * One tab, two jobs: Letters is a reference ("I can find out") and Words is a
 * collection ("look what I have"). Letters opens first for a beginner; once a
 * child knows fifty words, Words does, because by then that is the page worth
 * opening on.
 */

import { useEffect, useState } from 'react';
import type { Client, Collection } from './api.js';
import Letters from './Letters.js';
import Words from './Words.js';

/** From here on the collection is the page worth opening on. */
export const WORDS_FIRST_FROM = 50;

type Tab = 'letters' | 'words';

export default function Library({ client }: { readonly client: Client }) {
  const [tab, setTab] = useState<Tab | null>(null);
  const [collection, setCollection] = useState<Collection | null>(null);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => client.learner.collection.query())
      .then((found) => {
        if (!live) return;
        setCollection(found);
        setTab((was) => was ?? (found.total >= WORDS_FIRST_FROM ? 'words' : 'letters'));
      })
      .catch(() => {
        // Words will fetch again and say what went wrong; Letters needs nothing.
        if (live) setTab((was) => was ?? 'letters');
      });
    return () => {
      live = false;
    };
  }, [client]);

  const shown = tab ?? 'letters';
  const option = (value: Tab, label: string) => (
    <div className={shown === value ? 'on' : undefined}>
      <button
        type="button"
        aria-pressed={shown === value}
        style={{
          background: 'none',
          border: 0,
          font: 'inherit',
          color: 'inherit',
          width: '100%',
          minHeight: 32,
          cursor: 'pointer',
        }}
        onClick={() => {
          setTab(value);
        }}
      >
        {label}
      </button>
    </div>
  );

  return (
    <>
      <div className="pad" style={{ paddingTop: 10 }}>
        <div className="seg" role="group" aria-label="Letters or words">
          {option('letters', 'Letters')}
          {option('words', 'Words')}
        </div>
      </div>
      {/* Nothing until the first tab is known, so a child with a big collection
          does not see the alphabet flash up and vanish. */}
      {tab === null ? (
        <div className="grow" />
      ) : shown === 'letters' ? (
        <Letters client={client} />
      ) : (
        <Words client={client} preloaded={collection} />
      )}
    </>
  );
}
